import { NotificationItem as NotificationItemType } from "api/interfaces/notificationInterface.interface";
import * as s from "./NotificationStyle";
import NotificationItem from "./NotificationItem";
import { useNotification } from "./NotificationContainer";
import NotificationHeader from "./NotificationHeader";
import { useCallback, useEffect, useRef, useState } from "react";
import Spinner from "components/atoms/loader/Spinner";
import { FaArrowUp } from "react-icons/fa";
import styled from "styled-components";
import { Spin } from "antd";
import { COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";

export default function NotificationPopup() {
  const {
    notificationDropdownRef,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    count,
    isFetching,
  } = useNotification();
  const listRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement | null>(null);
  const [isAtTop, setIsAtTop] = useState(true);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0,
      rootMargin: "200px",
      root: listRef.current,
    });

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [observerCallback]);

  const handleScrollToTop = () => {
    const listElement = listRef.current;
    refetch();
    if (listElement) {
      listElement.scrollTop = 0;
    }
  };
  useEffect(() => {
    const getIsAtTop = () => {
      setIsAtTop(listRef.current?.scrollTop === 0);
    };
    getIsAtTop();
    window.addEventListener("scroll", getIsAtTop);
    return () => {
      window.removeEventListener("scroll", getIsAtTop);
    };
  }, []);

  return (
    <s.NotificationPopupWrapper ref={notificationDropdownRef}>
      <NotificationHeader />
      <ListComponent ref={listRef} className="infinite-list-component">
        {isAtTop && (isFetching || isLoading) && (
          <SpinnerContainer>
            <Spin size="large" />
          </SpinnerContainer>
        )}
        {data.length > 0 ? (
          <ul>
            {data.map((item, index) => (
              <NotificationItem
                item={item as unknown as NotificationItemType}
                key={index}
              />
            ))}
          </ul>
        ) : (
          <Empty>No notifications</Empty>
        )}
        {hasNextPage && (
          <NextButton ref={observerTarget}>
            <Spinner size={8} />
          </NextButton>
        )}
      </ListComponent>
      {count > 0 && data.length > 0 && !isFetching && !isLoading && (
        <NotificationNewArrival
          onClick={handleScrollToTop}
          data-testid="blocks-headernav-notification-notification-new-arrival-scroll-to-top"
        >
          <FaArrowUp />
          New notification received
        </NotificationNewArrival>
      )}
    </s.NotificationPopupWrapper>
  );
}

const NextButton = styled.div`
  height: 20px;
  width: 100%;
  padding-top: 10px;
`;

const ListComponent = styled.div`
  max-height: 500px;
  overflow: auto;
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 7px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background: ${COLORS.LIGHTGRAY};
    }
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Empty = styled.div`
  ${FLEX.FlexCenterCenter};
  width: 100%;
  height: 100%;
  padding: 50px 10px;
  color: ${({ theme }) => theme.COLORS.WHITE}90;
`;
const SpinnerContainer = styled.div`
  ${FLEX.FlexCenterCenter};
  width: 100%;
  height: 100%;
  .ant-spin .ant-spin-dot-holder {
    color: ${COLORS.ORANGE};
  }
`;

export const NotificationNewArrival = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 10px;
  ${FLEX.FlexCenterCenter};
  gap: 5px;
  flex-direction: column;
  background-color: ${COLORS.ORANGE};
  color: ${COLORS.WHITE};
  padding: 5px 10px;
  border-radius: 5px;
  font-size: ${calcRem(12)};
  cursor: pointer;
`;
